import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  Users, 
  MapPin, 
  Globe, 
  Mail,
  DollarSign,
  Loader2,
  ChevronLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LeagueDetailPage = () => {
  const { leagueId } = useParams();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeague();
  }, [leagueId]);

  const fetchLeague = async () => {
    try {
      const response = await axios.get(`${API_URL}/leagues/${leagueId}`);
      setLeague(response.data);
    } catch (error) {
      console.error('Failed to fetch league:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-bone pt-20">
        <div className="container-padding py-16 text-center">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-display text-2xl text-deep-green mb-2">League Not Found</h1>
          <Link to="/explore?tab=leagues">
            <Button className="mt-4">Back to Leagues</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-24">
      <div className="bg-deep-green py-12 md:py-16">
        <div className="container-padding">
          <Link to="/explore?tab=leagues" className="inline-flex items-center text-cream/70 hover:text-white mb-4">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Leagues
          </Link>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3">
            {league.name}
          </h1>
          <p className="text-cream/80 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {league.city}, {league.state}
          </p>
        </div>
      </div>

      <div className="container-padding py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {league.minority_affiliation_tags?.length > 0 && (
              <Card className="card-premium">
                <CardContent className="p-6">
                  <h2 className="font-display font-semibold text-xl text-deep-green mb-4">Affiliations</h2>
                  <div className="flex flex-wrap gap-2">
                    {league.minority_affiliation_tags.map((tag, i) => (
                      <Badge key={i} className="bg-gold/20 text-gold">{tag}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {league.season_info && (
              <Card className="card-premium">
                <CardContent className="p-6">
                  <h2 className="font-display font-semibold text-xl text-deep-green mb-4">Season</h2>
                  <p className="text-muted-foreground">{league.season_info}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="card-premium">
              <CardContent className="p-6 space-y-4">
                <h2 className="font-display font-semibold text-xl text-deep-green">Details</h2>
                
                {league.organizer_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Organizer</p>
                    <p className="font-semibold text-deep-green">{league.organizer_name}</p>
                  </div>
                )}
                
                {league.membership_cost && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-gold" />
                    <span className="font-semibold text-deep-green">${league.membership_cost}/year</span>
                  </div>
                )}

                {league.website && (
                  <a 
                    href={league.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gold hover:underline"
                  >
                    <Globe className="w-5 h-5" />
                    Visit Website
                  </a>
                )}

                {league.contact_email && (
                  <a 
                    href={`mailto:${league.contact_email}`}
                    className="flex items-center gap-2 text-muted-foreground hover:text-gold"
                  >
                    <Mail className="w-5 h-5" />
                    {league.contact_email}
                  </a>
                )}
              </CardContent>
            </Card>

            <Button className="w-full btn-gold">
              <Users className="w-4 h-4 mr-2" />
              Join League
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeagueDetailPage;
